/**
 * AJAX collection & search faceting via Shopify Section Rendering API.
 * Expects `product-facet` with `section-id` (main-collection / main-search).
 */
(() => {
  const pageType =
    window.themeVariables?.settings?.pageType ||
    (document.body.classList.contains("template-collection")
      ? "collection"
      : document.body.classList.contains("template-search")
        ? "search"
        : "");

  if (pageType !== "collection" && pageType !== "search") return;

  const { DrawerContent, PopoverContent } = window.ThemeCore || {};

  const defineCE = (name, ctor, options) => {
    if (!window.customElements.get(name)) {
      window.customElements.define(name, ctor, options);
    }
  };

  if (DrawerContent) {
    defineCE("facet-filters", class extends DrawerContent {});
  } else {
    defineCE(
      "facet-filters",
      class extends HTMLElement {
        get open() {
          return this.hasAttribute("open");
        }
        set open(value) {
          if (value) {
            this.setAttribute("open", "");
            document.documentElement.classList.add("lock-all");
          } else {
            this.removeAttribute("open");
            document.documentElement.classList.remove("lock-all");
          }
        }
        connectedCallback() {
          this.addEventListener("click", (e) => {
            if (e.target.closest(".drawer__overlay")) this.open = false;
          });
        }
      }
    );
  }

  if (PopoverContent) {
    defineCE("sort-by-popover", class extends PopoverContent {});
  } else {
    defineCE(
      "sort-by-popover",
      class extends HTMLElement {
        get open() {
          return this.hasAttribute("open");
        }
        set open(value) {
          if (value) {
            this.setAttribute("open", "");
            document.documentElement.classList.add("lock-mobile");
          } else {
            this.removeAttribute("open");
            document.documentElement.classList.remove("lock-mobile");
          }
        }
        connectedCallback() {
          this.addEventListener("click", (e) => {
            if (e.target.closest(".popover__overlay")) this.open = false;
          });
        }
      }
    );
  }

  if (!document.querySelector("product-facet[section-id]")) return;

  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), ms);
    };
  };

  const getFacetRoot = (el) => el && el.closest && el.closest("product-facet");

  const getSectionId = (facetRoot) => {
    const fromAttr = facetRoot.getAttribute("section-id");
    if (fromAttr) return fromAttr;
    const sec = facetRoot.closest("[id^='shopify-section-']");
    return sec ? sec.id.replace("shopify-section-", "") : null;
  };

  const getShopifySectionEl = (facetRoot) =>
    facetRoot.closest("[id^='shopify-section-']");

  const syncSortHiddenFromPopover = (facetRoot) => {
    const form = facetRoot.querySelector("#facet-filters-form");
    if (!form) return;
    const checked =
      facetRoot.querySelector("#sort-by-popover input[name='sort_by']:checked") ||
      facetRoot.querySelector("sort-by-popover input[name='sort_by']:checked");
    const hidden = form.querySelector("input[name='sort_by']");
    if (hidden && checked) hidden.value = checked.value;
  };

  const updateSortButtonLabel = (facetRoot) => {
    const checked =
      facetRoot.querySelector("#sort-by-popover input[name='sort_by']:checked") ||
      facetRoot.querySelector("sort-by-popover input[name='sort_by']:checked");
    const labelHost = facetRoot.querySelector("#sort-by-selected-value");
    if (!labelHost || !checked) return;
    const title = checked.getAttribute("title");
    if (title) {
      labelHost.textContent = title;
      return;
    }
    const item = checked.closest(".popover__choice-item");
    const lbl = item && item.querySelector(".popover__choice-label");
    if (lbl) labelHost.textContent = lbl.textContent.trim();
  };

  /** Filter / sort from facet form only (resets page when filters change). */
  const buildFetchUrlFromForm = (facetRoot) => {
    const sectionId = getSectionId(facetRoot);
    if (!sectionId) return null;
    syncSortHiddenFromPopover(facetRoot);
    const form = facetRoot.querySelector("#facet-filters-form");
    if (!form) return null;

    const u = new URL(window.location.pathname, window.location.origin);
    new URLSearchParams(new FormData(form)).forEach((val, key) => {
      u.searchParams.append(key, val);
    });
    u.searchParams.set("section_id", sectionId);
    return u;
  };

  /** Pagination or clear-filter links: trust href, add section_id. */
  const buildFetchUrlFromHref = (facetRoot, href) => {
    const sectionId = getSectionId(facetRoot);
    if (!sectionId) return null;
    const u = new URL(href, window.location.origin);
    u.searchParams.set("section_id", sectionId);
    return u;
  };

  let abortCtl = null;

  const applySectionHtml = (facetRoot, htmlText) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, "text/html");
    const sectionId = getSectionId(facetRoot);
    const current = getShopifySectionEl(facetRoot);
    if (!current || !sectionId) return;
    const incoming = doc.getElementById(`shopify-section-${sectionId}`);
    if (!incoming) return;
    current.innerHTML = incoming.innerHTML;
    document.documentElement.classList.remove("lock-all", "lock-mobile");
    current.dispatchEvent(
      new CustomEvent("facet:rendered", { bubbles: true, detail: { sectionId } })
    );
  };

  const setBusy = (facetRoot, busy) => {
    const main = facetRoot.querySelector("#facet-main") || facetRoot;
    main.setAttribute("aria-busy", busy ? "true" : "false");
  };

  const fetchAndRender = async (facetRoot, fetchUrl, pushUrl) => {
    if (window.Shopify && Shopify.designMode) return;
    if (!facetRoot || !fetchUrl) return;

    const sectionId = getSectionId(facetRoot);
    const sectionElId = sectionId ? `shopify-section-${sectionId}` : null;

    if (abortCtl) abortCtl.abort();
    abortCtl = new AbortController();

    setBusy(facetRoot, true);
    try {
      const res = await fetch(fetchUrl.toString(), {
        signal: abortCtl.signal,
        credentials: "same-origin",
        headers: { Accept: "text/html" },
      });
      if (!res.ok) throw new Error(String(res.status));
      const text = await res.text();
      applySectionHtml(facetRoot, text);
      if (pushUrl) {
        const clean = new URL(pushUrl, window.location.origin);
        clean.searchParams.delete("section_id");
        window.history.pushState(
          { facetAjax: true, url: clean.pathname + clean.search },
          "",
          clean.pathname + clean.search
        );
      }
    } catch (e) {
      if (e.name !== "AbortError") console.error("[collection-facets]", e);
    } finally {
      const root =
        (sectionElId && document.getElementById(sectionElId)?.querySelector("product-facet")) ||
        document.querySelector("product-facet[section-id]");
      if (root) setBusy(root, false);
    }
  };

  const scheduleFilterReload = debounce((facetRoot) => {
    const u = buildFetchUrlFromForm(facetRoot);
    if (!u) return;
    const push = new URL(u);
    push.searchParams.delete("section_id");
    fetchAndRender(facetRoot, u, push);
  }, 320);

  const onFormChange = (e) => {
    const form = e.target.closest("#facet-filters-form");
    if (!form) return;
    const facetRoot = getFacetRoot(form);
    if (!facetRoot) return;
    if (e.target.matches("input[type='number'], input[type='text']")) {
      scheduleFilterReload(facetRoot);
      return;
    }
    const u = buildFetchUrlFromForm(facetRoot);
    if (!u) return;
    const push = new URL(u);
    push.searchParams.delete("section_id");
    fetchAndRender(facetRoot, u, push);
  };

  document.addEventListener(
    "change",
    (e) => {
      const t = e.target;
      if (t.matches("#sort-by-popover input[name='sort_by'], sort-by-popover input[name='sort_by']")) {
        const facetRoot = getFacetRoot(t);
        if (!facetRoot) return;
        syncSortHiddenFromPopover(facetRoot);
        updateSortButtonLabel(facetRoot);
        const pop = t.closest("sort-by-popover");
        if (pop && "open" in pop) pop.open = false;
        const u = buildFetchUrlFromForm(facetRoot);
        if (!u) return;
        const push = new URL(u);
        push.searchParams.delete("section_id");
        fetchAndRender(facetRoot, u, push);
        return;
      }

      if (t.closest("#facet-filters-form")) {
        onFormChange(e);
      }
    },
    true
  );

  document.addEventListener(
    "input",
    (e) => {
      const t = e.target;
      if (!t.closest("#facet-filters-form")) return;
      if (!t.matches("input[type='number'], input[type='search']")) return;
      const facetRoot = getFacetRoot(t);
      if (facetRoot) scheduleFilterReload(facetRoot);
    },
    true
  );

  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id !== "facet-filters-form") return;
      const facetRoot = getFacetRoot(form);
      if (!facetRoot) return;
      e.preventDefault();
      const u = buildFetchUrlFromForm(facetRoot);
      if (!u) return;
      const push = new URL(u);
      push.searchParams.delete("section_id");
      fetchAndRender(facetRoot, u, push);
    },
    true
  );

  document.addEventListener("click", (e) => {
    const link = e.target.closest("a[href]");
    if (!link || !link.getAttribute("href") || link.getAttribute("href").startsWith("#")) return;

    const facetRoot = getFacetRoot(link);
    if (!facetRoot) return;

    if (link.closest(".pagination")) {
      e.preventDefault();
      const u = buildFetchUrlFromHref(facetRoot, link.href);
      if (!u) return;
      const push = new URL(link.href, window.location.origin);
      fetchAndRender(facetRoot, u, push);
      const anchor = facetRoot.querySelector("#facet-main") || facetRoot;
      anchor.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (link.matches("[data-action='clear-filters']")) {
      e.preventDefault();
      const u = buildFetchUrlFromHref(facetRoot, link.href);
      if (!u) return;
      const push = new URL(link.href, window.location.origin);
      fetchAndRender(facetRoot, u, push);
    }
  });

  window.addEventListener("popstate", () => {
    const facetRoot = document.querySelector("product-facet[section-id]");
    if (!facetRoot) return;
    const u = buildFetchUrlFromHref(facetRoot, window.location.href);
    if (!u) return;
    fetchAndRender(facetRoot, u, false);
  });
})();
